import { UserButton, useUser } from '@clerk/nextjs';
import { chatUnreadCountAtom } from '@web-app/atoms/chat/chatClientAtoms';
import { MENU_WIDTH } from '@web-app/components/consts';
import { ManagePracticeEnrollmentModal } from '@web-app/components/ManagePractice/ManagePracticeEnrollmentModal';
import { MfaPhoneModal } from '@web-app/components/MfaPhoneModal/MfaPhoneModal';
import { NavbarMenuItems } from '@web-app/components/Navbar/NavbarMenuItems';
import { NavbarSkeletons } from '@web-app/components/Navbar/NavbarSkeletons';
import { buildNavbarNotificationRoutes } from '@web-app/components/Navbar/utils';
import { BellIndicator } from '@web-app/components/Notifications';
import { useShellContributions } from '@web-app/components/Shell/ShellContributions';
import { SUPERVISION_STATUS } from '@web-app/constants/supervision';
import { useAccessNpReviewQueueStatus } from '@web-app/hooks/access-np-review/useAccessNpReviewQueueStatus';
import { useUnreadLabs } from '@web-app/hooks/prescription-review-v2/useUnreadLabs';
import { usePrescriptionTasks } from '@web-app/hooks/prescription-tasks/usePrescriptionTasks';
import { useNotifications } from '@web-app/hooks/queries/notifications';
import { useGetPendingFeeDecisions } from '@web-app/hooks/queries/pending-fee-decisions';
import { useReadyTasks } from '@web-app/hooks/queries/session-notes/tasks';
import { usePaginatedSupervisionTasksWithCursor } from '@web-app/hooks/queries/supervision-tasks/usePaginatedSupervisionTasksWithCursor';
import { useNewEnrollments } from '@web-app/hooks/queries/user/enrollments';
import { useIsBerryStreetOnly } from '@web-app/hooks/useIsBerryStreetOnly';
import { useIsPrescriber } from '@web-app/hooks/useIsPrescriber';
import { useOrganizationLogo } from '@web-app/hooks/whitelabel/useOrganizationLogo';
import { Task } from '@web-app/server/api/types';
import { useAdditionalBenefitsCheckEnabled } from '@web-app/utils/feature-flags/additional-benefits-check';
import { useChatEnabled } from '@web-app/utils/feature-flags/chat-enabled';
import { useConfirmCancellationEnabled } from '@web-app/utils/feature-flags/confirm-cancellation';
import { useGroupPracticeInvoiceEnabled } from '@web-app/utils/feature-flags/group-practice-invoice';
import { useInSessionTemplatesManagement } from '@web-app/utils/feature-flags/in-session-templates-management';
import { useManagePracticeDashboardEnabled } from '@web-app/utils/feature-flags/manage-practice-dashboard';
import { useMfaPhonePromptEnabled } from '@web-app/utils/feature-flags/mfa-phone-prompt';
import { useNonAppointmentHoursTrackingEnabled } from '@web-app/utils/feature-flags/non-appointment-hours-tracking';
import { usePerformanceDashboardEnabled } from '@web-app/utils/feature-flags/performance-dashboard';
import { usePrescriptionTasksEnabled } from '@web-app/utils/feature-flags/prescription-tasks';
import { useProviderAttestationDocuments } from '@web-app/utils/feature-flags/provider-attestation-documents';
import { useProviderCalendarEnabled } from '@web-app/utils/feature-flags/provider-calendar';
import { useSettingsPageAccess } from '@web-app/utils/feature-flags/settings-page-access';
import { useSubOrgsInvoiceReviewEnabled } from '@web-app/utils/feature-flags/sub-orgs-invoice-review';
import { useSupervisionTasksEnabled } from '@web-app/utils/feature-flags/supervision-tasks';
import { useW9PageEnabled } from '@web-app/utils/feature-flags/w9-page';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface NavbarProps {
  logoColor?: 'dark' | 'white';
}

const Navbar = ({ logoColor = 'white' }: NavbarProps): JSX.Element => {
  const router = useRouter();
  const { user } = useUser();
  const settingsPageAccess = useSettingsPageAccess();
  const logoConfig = useOrganizationLogo();
  const managePracticeDashboard = useManagePracticeDashboardEnabled();
  const isCalendarEnabled = useProviderCalendarEnabled();
  const performanceDashboard = usePerformanceDashboardEnabled();
  const supervisionTasks = useSupervisionTasksEnabled();
  const isBerryStreetOnly = useIsBerryStreetOnly();
  const isGroupPracticeInvoiceEnabled = useGroupPracticeInvoiceEnabled();
  const isW9PageEnabled = useW9PageEnabled();
  // The In-Session Templates nav link is gated by the management flag.
  const inSessionTemplatesNavEnabled = useInSessionTemplatesManagement();
  const documentsNavEnabled = useProviderAttestationDocuments();

  const isConfirmCancellationEnabled = useConfirmCancellationEnabled();
  const isAdditionalBenefitsCheckEnabled = useAdditionalBenefitsCheckEnabled();
  const isMfaPhonePromptEnabled = useMfaPhonePromptEnabled();
  const isChatEnabled = useChatEnabled();
  const isSubOrgsInvoiceReviewEnabled = useSubOrgsInvoiceReviewEnabled();
  const { enabled: isNonAppointmentHoursEnabled } =
    useNonAppointmentHoursTrackingEnabled();
  const prescriptionTasksEnabled = usePrescriptionTasksEnabled();
  const messagesCount = useAtomValue(chatUnreadCountAtom);

  const { isPrescriber, isLoading: isPrescriberStatusLoading } =
    useIsPrescriber();
  const contributions = useShellContributions();
  const navEntries = Object.fromEntries(
    contributions.flatMap(({ nav }) => (nav ? [[nav.path, nav]] : [])),
  );
  const pinnedSection = contributions.find(({ bell }) => bell)?.bell;

  // Assignment-derived visibility for the ACCESS NP review queue (capability
  // fact, like is_prescriber): shown only to providers with assigned patients.
  const { data: accessNpQueueStatus } = useAccessNpReviewQueueStatus();
  const hasAccessNpReviewQueue = accessNpQueueStatus?.hasQueue ?? false;

  const {
    data: tasks,
    isPending: isTasksLoading,
  }: { data?: Task[]; isPending: boolean } = useReadyTasks();
  // Use paginated hook for pending tasks to get the count (only need first page)
  const {
    totalCount: pendingTotalCount,
    isLoading: isSupervisionTasksLoading,
    canSupervise: isSupervisor,
  } = usePaginatedSupervisionTasksWithCursor(SUPERVISION_STATUS.PENDING);
  const {
    data: enrollments,
    isPending: isEnrollmentsLoading,
  }: { data?: string[]; isPending: boolean } = useNewEnrollments();
  const { data: cancellationDecisions } = useGetPendingFeeDecisions({
    enabled: isConfirmCancellationEnabled,
  });
  const { data: prescriptionTasksList } = usePrescriptionTasks({
    page: 1,
    pageSize: 1,
    enabled: prescriptionTasksEnabled && isPrescriber,
  });

  const tasksCount = tasks?.filter((task) => !task.isSessionInProgress).length;
  const supervisionTasksCount = pendingTotalCount ?? 0;
  const enrollmentsCount = enrollments?.length;
  const cancellationDecisionsCount =
    cancellationDecisions?.filter((d) => !d.alreadyAnsweredOrCharged).length ??
    0;
  const prescriptionTasksCount = prescriptionTasksList?.totalCount ?? 0;
  const { data: unreadLabs } = useUnreadLabs(Boolean(user) && isPrescriber);
  const unreadLabCount = unreadLabs?.awaitingInformationCount ?? 0;

  const { data: notifications } = useNotifications();
  const notificationRoutes = buildNavbarNotificationRoutes(
    notifications,
    unreadLabs?.latestPatientHealthieId,
  );

  const isAnyDataLoading =
    isTasksLoading ||
    isSupervisionTasksLoading ||
    isEnrollmentsLoading ||
    isPrescriberStatusLoading;

  const isInternalUser =
    user?.primaryEmailAddress?.emailAddress?.endsWith('@berrystreet.co') ??
    false;
  const isAdminMode = router.query.admin === 'true';
  const showInternalTools = isInternalUser && isAdminMode;

  return (
    <>
      {managePracticeDashboard && !!enrollmentsCount && (
        <ManagePracticeEnrollmentModal enrollments={enrollments} />
      )}
      {isMfaPhonePromptEnabled && <MfaPhoneModal />}

      <aside
        style={{ width: MENU_WIDTH }}
        className="hide-scroll fixed left-0 top-0 z-10 flex h-screen shrink-0 flex-col overflow-y-auto bg-sidebar text-sidebar-foreground"
      >
        <div className="flex w-full items-center gap-2.5 px-6 py-11">
          <Link
            href="/"
            className="block flex-1 rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring"
          >
            {logoConfig && (
              <Image
                src={logoConfig.getLogoSrc({ logoColor })}
                alt={logoConfig.altText}
                width={160}
                height={40}
                style={{
                  objectFit: 'contain',
                  filter:
                    logoColor === 'white'
                      ? logoConfig.whiteLogoFilter
                      : undefined,
                }}
              />
            )}
          </Link>
          <BellIndicator pinned={pinnedSection} />
        </div>

        <nav className="flex w-full flex-1 flex-col gap-4 p-2">
          {isAnyDataLoading ? (
            <NavbarSkeletons
              settingsPageAccess={settingsPageAccess}
              managePracticeDashboard={managePracticeDashboard}
              calendarEnabled={isCalendarEnabled}
              performanceDashboard={performanceDashboard}
              showInternalTools={showInternalTools}
              supervisionTasks={supervisionTasks}
              isBerryStreetOnly={isBerryStreetOnly}
              groupPracticeInvoiceEnabled={isGroupPracticeInvoiceEnabled}
              w9PageEnabled={isW9PageEnabled}
              confirmCancellationEnabled={isConfirmCancellationEnabled}
              additionalBenefitsCheckEnabled={isAdditionalBenefitsCheckEnabled}
              chatEnabled={isChatEnabled}
              navEntries={navEntries}
              subOrgsInvoiceReviewEnabled={isSubOrgsInvoiceReviewEnabled}
              nonAppointmentHoursEnabled={isNonAppointmentHoursEnabled}
              inSessionTemplatesNavEnabled={inSessionTemplatesNavEnabled}
              documentsNavEnabled={documentsNavEnabled}
              isPrescriber={isPrescriber}
              prescriptionTasksEnabled={prescriptionTasksEnabled}
              hasAccessNpReviewQueue={hasAccessNpReviewQueue}
            />
          ) : (
            <NavbarMenuItems
              settingsPageAccess={settingsPageAccess}
              managePracticeDashboard={managePracticeDashboard}
              calendarEnabled={isCalendarEnabled}
              performanceDashboard={performanceDashboard}
              showInternalTools={showInternalTools}
              isSupervisor={isSupervisor}
              supervisionTasks={supervisionTasks}
              isBerryStreetOnly={isBerryStreetOnly}
              groupPracticeInvoiceEnabled={isGroupPracticeInvoiceEnabled}
              w9PageEnabled={isW9PageEnabled}
              confirmCancellationEnabled={isConfirmCancellationEnabled}
              additionalBenefitsCheckEnabled={isAdditionalBenefitsCheckEnabled}
              chatEnabled={isChatEnabled}
              navEntries={navEntries}
              subOrgsInvoiceReviewEnabled={isSubOrgsInvoiceReviewEnabled}
              nonAppointmentHoursEnabled={isNonAppointmentHoursEnabled}
              inSessionTemplatesNavEnabled={inSessionTemplatesNavEnabled}
              documentsNavEnabled={documentsNavEnabled}
              isPrescriber={isPrescriber}
              prescriptionTasksEnabled={prescriptionTasksEnabled}
              hasAccessNpReviewQueue={hasAccessNpReviewQueue}
              enrollmentsCount={enrollmentsCount || 0}
              accessPendingReviewCount={
                accessNpQueueStatus?.pendingReviewCount ?? 0
              }
              tasksCount={tasksCount || 0}
              supervisionTasksCount={supervisionTasksCount}
              cancellationDecisionsCount={cancellationDecisionsCount}
              prescriptionTasksCount={prescriptionTasksCount}
              unreadLabCount={unreadLabCount}
              messagesCount={messagesCount}
              notificationRoutes={notificationRoutes}
            />
          )}
        </nav>

        <div className="flex w-full items-center gap-4 px-4 py-8">
          <UserButton />
          <span className="text-base font-medium text-sidebar-foreground">
            Account
          </span>
        </div>
      </aside>
    </>
  );
};

export default Navbar;
